import { useDispatch, useSelector } from 'react-redux'
import PaymentForm from "./PaymentForm";
import DelayClock from "../components/DelayClock";
import ModalIframe from "../components/ModalIframe";
import { useState, useEffect, useRef } from 'react';
import { getUrlAssets } from '../constants'
import { selectPaymentBtnText, selectI18nPaymentBtnText, selectMockResponse, selectFirstView, selectFormValid, paymentMethodSlice, selectLoading as selectPaymentMethodLoading, selectNextAction, selectIsMethodSwitch } from '../app/reducers/paymentMethod'
import { selectId as selectOperationId, selectCaptureConfigurations, operationSlice } from '../app/reducers/operation'
import { selectPristine as delayClockPristine, selectIsTicking, setIsTicking, selectDelay, selectTimeLeft, setTimeLeft, selectSkipDelay } from '../app/reducers/delayClock'
import { useExecutePaymentMethodMutation } from '../app/services/paymentMethod'
import { selectList } from '../app/reducers/methodsList'
import { selectIntegrations } from '../app/reducers/integrations'
import { selectLanguage } from '../app/reducers/language'
import { selectChallengeURL, selectChallengeInProgress } from '../app/reducers/authentication';
import { selectSettings as selectConfigSettings } from '../app/reducers/flexConfiguration';
import OccurrenceRegistry from '../components/OccurrenceRegistry';
import { useTranslation } from '../hooks/useTranslation';
import { resolveAcquirerLogo, resolveAcquirerLogoAlt } from '../utils/acquirerLogo';

export default function PaymentMethod({ pos, method, chooseMethod, methodChoosed, responseCallback, trackingCallback, onErrorCallback }) {
    const popupRef = useRef(null);
    const [contentVisible, setContentVisible] = useState(true);
    const [showMethodSpinner, setShowMethodSpinner] = useState(false);
    const dispatch = useDispatch();
    const operation = useSelector(operationSlice.selectSlice);
    const operationId = useSelector(selectOperationId);
    const paymentMethod = useSelector(paymentMethodSlice.selectSlice);
    const mockResponse = useSelector(selectMockResponse);
    const nextAction = useSelector(selectNextAction);
    const paymentMethodLoading = useSelector(selectPaymentMethodLoading);
    const isMethodSwitch = useSelector(selectIsMethodSwitch);
    const [executePaymentMethod] = useExecutePaymentMethodMutation();
    const paymentBtnText = useSelector(selectPaymentBtnText);
    const i18nPaymentBtnText = useSelector(selectI18nPaymentBtnText);
    const isFirstView = useSelector(selectFirstView);
    const isFormValid = useSelector(selectFormValid);
    const clockPristine = useSelector(delayClockPristine);
    const delay = useSelector(selectDelay);
    const isTicking = useSelector(selectIsTicking);
    const timeLeft = useSelector(selectTimeLeft);
    const skipDelay = useSelector(selectSkipDelay);
    const configSettings = useSelector(selectConfigSettings);
    const onStartOfDelay = () => { dispatch(setIsTicking(true)) };
    const onDelayTick = (count) => { dispatch(setTimeLeft(count)) };
    const onEndOfDelay = () => { dispatch(setIsTicking(false)); onSubmit() };
    const methodsList = useSelector(selectList);
    const integrations = useSelector(selectIntegrations);
    const language = useSelector(selectLanguage);
    const challengeURL = useSelector(selectChallengeURL);
    const challengeInProgress = useSelector(selectChallengeInProgress);
    const { t, tMixed } = useTranslation();

    const captureConfigurations = useSelector(selectCaptureConfigurations);
    const [logoSrcOverride, setLogoSrcOverride] = useState(null);
    const [logoHidden, setLogoHidden] = useState(false);

    const defaultAcquirerLogo = getUrlAssets() + "/payme-logo.svg";
    const resolvedLogoUrl = resolveAcquirerLogo({
      localSettings: configSettings,
      captureConfigurations,
      defaultUrl: defaultAcquirerLogo
    });
    // logoSrcOverride solo se llena tras un fallo de carga, para degradar al asset del CDN
    const effectiveLogoSrc = logoSrcOverride || resolvedLogoUrl;

    const altOverride = resolveAcquirerLogoAlt({ localSettings: configSettings, captureConfigurations });
    const translatedAlt = t('payment_method.acquirer_logo_alt');
    const acquirerLogoAlt = altOverride
      ? tMixed(altOverride)
      // useTranslation devuelve la propia clave cuando no la encuentra; se evita
      // que ese literal termine en el atributo alt.
      : (translatedAlt === 'payment_method.acquirer_logo_alt' ? 'Logo del adquirente' : translatedAlt);

    // Al cambiar la URL resuelta se reinicia el estado de fallo, para que el
    // limite de 1 intento aplique por URL y no por montaje completo.
    useEffect(() => {
      setLogoSrcOverride(null);
      setLogoHidden(false);
    }, [resolvedLogoUrl]);

    const handleAcquirerLogoError = () => {
      console.error({ acquirerLogoLoadError: effectiveLogoSrc });
      if (effectiveLogoSrc !== defaultAcquirerLogo) {
        setLogoSrcOverride(defaultAcquirerLogo);
      } else {
        setLogoHidden(true);
      }
    };

    // Fade crossfade: cuando empieza un method switch, fade out el contenido
    useEffect(() => {
      if (isMethodSwitch && methodChoosed) {
        setContentVisible(false);
        const timer = setTimeout(() => setShowMethodSpinner(true), 140);
        return () => clearTimeout(timer);
      }
    }, [isMethodSwitch, methodChoosed]);

    // Fade in: cuando loading termina (nuevo método cargado)
    useEffect(() => {
      if (!paymentMethodLoading && !isMethodSwitch && methodChoosed) {
        setShowMethodSpinner(false);
        const timer = setTimeout(() => {
          setContentVisible(true);
        }, 16);
        return () => clearTimeout(timer);
      }
    }, [paymentMethodLoading, isMethodSwitch, methodChoosed]);

    const getAmountValue = (amount, exponent, amount_part) => {
      if (amount === null || amount === undefined) {
        throw new Error("amount is required");
      }
      if (!Number.isInteger(exponent) || exponent < 0) {
        throw new Error("exponent must be a non-negative integer");
      }
      if (amount_part !== "integer" && amount_part !== "decimal") {
        throw new Error("amount_part must be 'integer' or 'decimal'");
      }
      // 1) Normalizar a string y eliminar separadores por si vinieran
      let s = String(amount).trim().replace(/[.,]/g, "");
      // 2) Validar solo dígitos
      if (!/^\d+$/.test(s)) {
        throw new Error(`Invalid amount format: ${amount}`);
      }
      // 3) Asegurar al menos exponent dígitos para la parte decimal
      //    (si exponent=2 y s="5" => "05")
      if (exponent > 0) s = s.padStart(exponent, "0");
      // 4) Partir desde la derecha
      const dec = exponent > 0 ? s.slice(-exponent) : "";
      let intPart = exponent > 0 ? s.slice(0, -exponent) : s;
      // 5) Normalizar parte entera: quitar ceros a la izquierda, pero si queda vacío => "0"
      intPart = intPart.replace(/^0+/, "");
      if (intPart === "") intPart = "0";
      return amount_part === "integer" ? intPart : dec;
    };

    function buildWrapperClassName() {
        return "payment-method-wrapper " + method.id + " " + (methodChoosed ? " selected" : "")
    }

    // --- Movido a PaymentMethodSidebar.js ---
    // function buildChooseBtnClassName() {
    //     return "payment-choose-btn choose-btn-" + (pos + 1) + (methodChoosed ? " selected" : "")
    // }

    function getCurrencySymbol(){
      let code=operation.operation.payment_details.currency;
      switch(code){
        case "604": return "S/";
        case "840": return "$";
        default: return "";
      }
    }

    async function onSubmit() {
      if(paymentMethodLoading){
        console.info(`Cant execute because paymentMethodLoading`);
        return;
      }
      if(isFirstView && !isFormValid){
        console.info(`Cant execute ${method.id}, invalid Form`);
        return;
      }
      if(nextAction!=undefined){
        if(nextAction.type=="open_popup"){
          console.info(`Cant execute, there is a popup`);
          return;
        }
        if(nextAction.type=="open_iframe_modal"){
          console.info(`Cant execute, there is a modal`);
          return;
        }
        if(nextAction.type=="execute_response_callback"){
          console.info(`Llamando al responseCallback`);
          let responseData=JSON.parse(JSON.stringify(nextAction.data));
          if("url" in responseData)
            delete responseData.url;
          if("image" in responseData)
            delete responseData.image;
          responseCallback(responseData);
          // responseCallback(nextAction.secured_data, nextAction.signature);
          return;
        }
        if(nextAction.type=="reset_payment_method"){
          if(!configSettings.display_result_screen){
            console.info(`Llamando al responseCallback para una denegada o transaccion de error`);
            let responseData=JSON.parse(JSON.stringify(nextAction.data));
            if("url" in responseData)
              delete responseData.url;
            if("image" in responseData)
              delete responseData.image;
            responseCallback(responseData);
          }else{
            chooseMethod(method, operation);
          }
          return;
        }
      }
      console.info(`Executing ${method.id} for operation ${operationId}`);
      executePaymentMethod({
        operation,
        paymentMethod,
        integrations,
        locale: language
      });
      if(paymentMethod.code=="CUOTEALO" || paymentMethod.code=="BANK_TRANSFER"){
        console.log("abriendo metodo "+paymentMethod.code);
        popupRef.current = window.open('', '_blank');
        window.flex_pop_up_poqwieurndf30_ref = popupRef.current;
        OccurrenceRegistry.registerOccurrence("cuotealo");
        try {
          let result=await OccurrenceRegistry.waitForOccurrence("cuotealo");
          popupRef.current.location.href = result.data;
        } catch (err) {
          console.error("fallo window.open");
          popupRef.current.close();
        }
      }
        
    }
    if (!methodChoosed) return null;
    return (
        <div className={buildWrapperClassName()}>
        
        {methodChoosed && nextAction!=undefined && nextAction.type=="open_iframe_modal" && <ModalIframe url={nextAction.data.url} />}
        {methodChoosed && challengeInProgress && <ModalIframe url={challengeURL} />}

      {/* --- Movido a PaymentMethodSidebar.js ---
      {methodsList.length>1 && <div className={buildChooseBtnClassName()} onClick={() => chooseMethod(method, operation)}>
        <img src={method.small_icon} className={"sm-nav-payment-method-icon"} /><span className={"lbl-payment-method-name"}>{'i18nKey' in method ? t(method.i18nKey) : method.name}</span>
      </div>}
      */}
      {showMethodSpinner && <div className={"method-switch-spinner"}><div className={"spinner-small"}></div></div>}
      {methodChoosed &&
      <div className={"payment-method-form-wrapper"+((methodsList.length>1)?"":" payment-method-form-single")+(contentVisible ? " fade-visible" : " fade-hidden")}>
        <div className={"payment-method-form-content"}>
          <PaymentForm />
        </div>
        <div className={"payment-method-payme-section"}>
          {!logoHidden && <img src={effectiveLogoSrc} alt={acquirerLogoAlt} onError={handleAcquirerLogoError} />}
        </div>
        <div className={"payment-method-amount-detail"}>
          <div className={"amount-label"}>{t('common.pay_full')}</div>
          <div className={"amount-value"}><span className={"currency"}>{getCurrencySymbol()}</span><span className={"integer-part"}>{getAmountValue(operation.operation.payment_details.amount,2,"integer")}</span><span className={"decimal-part"}>.{getAmountValue(operation.operation.payment_details.amount,2,"decimal")}</span></div>
        </div>
        <div className={"payment-method-submit-btn"+((isFirstView && !isFormValid)?" disabled":"")}>
          <DelayClock onEndOfDelay={onEndOfDelay} delay={delay} onStartOfDelay={onStartOfDelay} onDelayTick={onDelayTick} skipDelay={skipDelay}/>
          <button type="button" onClick={e=>{onSubmit()}}>{isTicking ? tMixed(i18nPaymentBtnText || paymentBtnText) + " " + t('common.in')+" " + timeLeft + " " + t('common.secs')+" " : tMixed(i18nPaymentBtnText || paymentBtnText)}</button>
        </div>
      </div>}
    </div>
    )
}
